import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Chip } from '@c2fo/liquidity';
import { useSchedulesState } from '../../contexts';
import { LoadingOverlay } from '../LoadingSpinner';
import { BackLink } from '../BackLink';
import { Card } from '../Card';
import { CardHeader } from '../CardHeader';
import { CardSection } from '../CardSection';
import { DetailField } from '../DetailField';
import { EditButton } from '../EditButton';
import { FieldsContainer } from '../FieldsContainer';
import { EntityLink } from '../EntityLink';
import { NotFoundCard } from '../NotFoundCard';
import { PageContainer } from '../PageContainer';
import { DeliveryActionsDisplay } from '../DeliveryActionsEditor';
import { RetryConfigDisplay } from '../RetryConfigEditor';
import { getScheduleStateChipType, getScheduleStateLabel } from '../../utils';
import { formatDateWithTimezone } from '../../../../utils/dateFormat';

export const ScheduleView: React.FC = () => {
  const { scheduleUid } = useParams<{ scheduleUid: string }>();
  const navigate = useNavigate();
  const {
    currentSchedule: schedule,
    isLoadingSchedule: isLoading,
    fetchSchedule,
    clearCurrentSchedule,
  } = useSchedulesState();

  useEffect(() => {
    if (scheduleUid) {
      // Construct full path: scheduledDeliveries/{scheduleUid}
      fetchSchedule(`scheduledDeliveries/${scheduleUid}`);
    }

    return () => {
      clearCurrentSchedule();
    };
  }, [scheduleUid, fetchSchedule, clearCurrentSchedule]);

  if (isLoading) {
    return <LoadingOverlay message="Loading schedule..." />;
  }

  if (!schedule) {
    return (
      <PageContainer>
        <BackLink to="/file-delivery/scheduledDeliveries" label="Back to Schedules" />
        <NotFoundCard message="Schedule not found or could not be loaded." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackLink to="/file-delivery/scheduledDeliveries" label="Back to Schedules" />

      <Card>
        <CardHeader
          chip={
            <Chip
              label={getScheduleStateLabel(schedule.state)}
              type={getScheduleStateChipType(schedule.state)}
              variant="filled"
              size="xs"
            />
          }
          title={schedule.displayName || 'Scheduled Delivery'}
          description={schedule.scheduleDescription}
          actions={<EditButton onClick={() => navigate(`/file-delivery/scheduledDeliveries/${scheduleUid}/edit`)} />}
        />
        <FieldsContainer>
          <DetailField
            label="Schedule Type"
            value={<Chip label={schedule.scheduleTypeLabel} type="secondary" variant="outlined" size="xs" />}
          />
          <DetailField
            label="Source Location"
            value={
              schedule.sourceLocation ? (
                <EntityLink to={`/file-delivery/${schedule.sourceLocation.name}`}>
                  {schedule.sourceLocation.displayName}
                </EntityLink>
              ) : (
                'Unknown'
              )
            }
          />
          <DetailField label="Source Path" value={schedule.sourcePath} mono />
          <DetailField
            label="Target Location"
            value={
              schedule.targetLocation ? (
                <EntityLink to={`/file-delivery/${schedule.targetLocation.name}`}>
                  {schedule.targetLocation.displayName}
                </EntityLink>
              ) : (
                'Unknown'
              )
            }
          />
          <DetailField label="Target Path" value={schedule.targetPath} mono />
          <DetailField label="UID" value={schedule.uid} mono />
          <DetailField label="Resource Name" value={schedule.name} mono />
          <DetailField
            label="Next Execution"
            value={schedule.nextExecutionTime ? formatDateWithTimezone(schedule.nextExecutionTime) : undefined}
          />
          <DetailField
            label="Last Execution"
            value={schedule.lastExecutionTime ? formatDateWithTimezone(schedule.lastExecutionTime) : undefined}
          />
          <DetailField
            label="Created"
            value={schedule.createTime ? formatDateWithTimezone(schedule.createTime) : undefined}
          />
        </FieldsContainer>

        <CardSection title="Delivery Actions">
          <DeliveryActionsDisplay
            preDeliveryAction={schedule.preDeliveryAction}
            postDeliveryAction={schedule.postDeliveryAction}
          />
        </CardSection>

        <CardSection title="Retry Configuration">
          <RetryConfigDisplay retryConfig={schedule.retryConfig} />
        </CardSection>
      </Card>
    </PageContainer>
  );
};
